import type { Season, SeasonBooleanField } from "@/features/seasons/models/season";
import { useSeasonDraft } from "@/features/seasons/pages/list/hooks/use-season-settings";
import { getSeasonUiTypeOptions, isSeasonUiTypeEditable } from "@/features/seasons/lib/season-settings";
import { Hint, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Switch } from "@cmde/shadcn";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

export function SeasonColumnHint({ label, tooltip }: { label: string; tooltip: string }) {
  return (
    <Hint content={tooltip}>
      <span className="cursor-help">{label}</span>
    </Hint>
  );
}

export function SeasonUiTypeField({ season, sport }: { season: Season; sport: string }) {
  const { t } = useTranslation();
  const { draft, updateDraft, isPending } = useSeasonDraft(season);
  const options = getSeasonUiTypeOptions(sport);
  const editable = isSeasonUiTypeEditable(sport);

  if (!editable) {
    return <span className="text-muted-foreground">{t("hockey.not_available")}</span>;
  }

  return (
    <Select
      value={draft.uiType}
      disabled={isPending}
      onValueChange={(uiType) => updateDraft({ uiType: uiType as Season["uiType"] })}
    >
      <SelectTrigger size="sm" className="min-w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {t(option.labelKey)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function SeasonRosterField({ season }: { season: Season }) {
  const { t } = useTranslation();
  const { draft, updateDraft, isPending } = useSeasonDraft(season);

  return (
    <Select
      value={draft.defaultRoster}
      disabled={isPending}
      onValueChange={(defaultRoster) => updateDraft({ defaultRoster: defaultRoster as Season["defaultRoster"] })}
    >
      <SelectTrigger size="sm" className="min-w-40">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="season">{t("seasons.list.roster.season")}</SelectItem>
        <SelectItem value="fixture">{t("seasons.list.roster.fixture")}</SelectItem>
      </SelectContent>
    </Select>
  );
}

export function SeasonBooleanField({
  season,
  field,
  label,
}: {
  season: Season;
  field: SeasonBooleanField;
  label: string;
}) {
  const { draft, updateDraft, isPending } = useSeasonDraft(season);

  return (
    <Switch
      checked={draft[field]}
      disabled={isPending}
      onCheckedChange={(checked) => updateDraft({ [field]: checked })}
      aria-label={label}
    />
  );
}

export function SeasonMatchLockTimeField({ season }: { season: Season }) {
  const { draft, updateDraft, isPending } = useSeasonDraft(season);
  const [value, setValue] = useState(String(draft.matchLockTime));

  useEffect(() => {
    setValue(String(draft.matchLockTime));
  }, [draft.matchLockTime]);

  return (
    <Input
      type="number"
      min={0}
      value={value}
      disabled={isPending}
      className="w-20"
      onChange={(event) => {
        const nextValue = event.target.value;
        setValue(nextValue);
        const parsed = Number(nextValue);
        if (nextValue !== "" && !Number.isNaN(parsed)) {
          updateDraft({ matchLockTime: parsed });
        }
      }}
    />
  );
}
